import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigation, useRoute } from "@react-navigation/native";
import { Dimensions, TouchableOpacity } from "react-native";
import { AntDesign, MaterialCommunityIcons } from "@expo/vector-icons";
import { StyleSheet } from "react-native";
import { ScrollView } from "react-native-gesture-handler";
import { useDispatch, useSelector } from "react-redux";
import { getMergedTrackerOrders } from "../../../redux/dispatcher/chinaTrackerDispatcher";
import { Text, View } from "../../../components/shared/Themed";
import TrackerEnquiryModal from "./TrackerEnquiryModal";
import TrackerHistoryModal from "./TrackerHistoryModal";
import Spinner from "../../../components/shared/Spinner";
import { formatLocalDate } from "../../../utils/common";
import { translateFields, useTranslation } from "../../../utils/useTranslation";
import { chinaTrackerTranslationMap } from "../../../utils/translations";
import ChinaTrackerFlagModal from "./ChinaTrackerFlagModal";
import ChinaShipmentsListModal from "./ChinaShipmentsListModal";
import { useAppTheme } from "../../../hooks/useAppTheme";


const ChinaTrackerDetailsReport = () => {
  const { colors: themeColors } = useAppTheme();
  const styles = createStyles(themeColors);
  const route = useRoute();

  // Order Params And State
  const { order } = route.params as { order: any };
  const [selectedOrder, setSelectedOrder] = useState<any>(null);

  const [trackingData, setTrackingData] = useState<any>([]);
  const [progressBar, setProgressBar] = useState<number>(0);
  const [trackerAction, setTrackerAction] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>();
  const onClose = () => {
    setModalOpen(false);
  };
  const [windowWidth, setWindowWidth] = useState(
    Dimensions.get("window").width,
  );
  const navigation = useNavigation();
  const { loading, filteredOrders, orders, isProductTrackerLoaded } =
    useSelector((state: any) => state.chinaTracker);
  const dispatch = useDispatch<any>();

  const fetchTrackerOrders = useCallback(() => {
    dispatch(getMergedTrackerOrders({ refresh: false }));
  }, [dispatch]);

  const { t } = useTranslation();

  // Fetch orders if not available
  useEffect(() => {
    if ((!orders || (orders?.length || 0) === 0) && !isProductTrackerLoaded) {
      fetchTrackerOrders();
    }
  }, [orders, isProductTrackerLoaded, fetchTrackerOrders]);

  // Main order processing effect
  useEffect(() => {
    // Early return if order is invalid
    if (!order || typeof order === "string") {
      if (navigation.canGoBack()) {
        // navigation.goBack();
        navigation.navigate("Root", {
          screen: "Home",
          params: {
            screen: "Tracker",
            params: {
              screen: "ChinaTrackerReport",
              params: {
                screen: "ChinaTrackerScreen",
              },
            },
          },
        });
      } else {
        navigation.navigate("Root", {
          screen: "Home",
          params: {
            screen: "Tracker",
            params: {
              screen: "ChinaTrackerReport",
              params: {
                screen: "ChinaTrackerScreen",
              },
            },
          },
        });
      }
      return;
    }

    let orderToUse: any = order;

    // Handle merged orders - find the full order data if needed
    if (!order.details && (order.key || order.Key)) {
      // Wait for filteredOrders to be available before processing
      if (!filteredOrders || filteredOrders.length === 0) {
        return; // Exit early, will re-run when filteredOrders is available
      }

      const orderKey = order.key || order.Key;
      const retrievedOrder = filteredOrders.find(
        (orderElement: any) =>
          orderElement.Key === orderKey || orderElement.key === orderKey,
      );

      if (retrievedOrder) {
        orderToUse = retrievedOrder;
      } else {
        console.warn(`Order with key ${orderKey} not found in filteredOrders`);
        // You might want to handle this case - perhaps show an error or navigate away
        return;
      }
    }

    // Only update if the selected order has actually changed
    setSelectedOrder((prevSelected: any | null) => {
      // Compare using a stable identifier
      const prevKey = prevSelected?.Key || prevSelected?.key;
      const newKey = orderToUse.Key || orderToUse.key;

      if (!prevSelected || prevKey !== newKey) {
        return orderToUse;
      }
      return prevSelected;
    });
  }, [order, filteredOrders, navigation]);

  // Separate useEffect for screen size tracking
  useEffect(() => {
    const subscription = Dimensions.addEventListener("change", ({ window }) => {
      setWindowWidth(window.width);
    });

    return () => subscription?.remove();
  }, []); // Empty dependency array since this doesn't depend on any props/state

  // Optional: Loading state management
  const isOrderReady = useMemo(() => {
    if (!order || typeof order === "string") return false;

    // If it needs filteredOrders to resolve, check if they're available
    if (!order.OrdersMerged && (order.key || order.Key)) {
      return filteredOrders && filteredOrders.length > 0;
    }

    return true;
  }, [order, filteredOrders]);

  const DrawerHeader = () => {
    return (
      <View style={styles.drawerHeaderStyle}>
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <TouchableOpacity
            style={{ paddingHorizontal: 15, paddingVertical: 5 }}
            onPress={() => {
              if (navigation.canGoBack()) {
                // navigation.goBack();
                navigation.navigate("Root", {
                  screen: "Home",
                  params: {
                    screen: "Tracker",
                    params: {
                      screen: "ChinaTrackerReport",
                      params: {
                        screen: "ChinaTrackerScreen",
                      },
                    },
                  },
                });
              } else {
                navigation.navigate("Root", {
                  screen: "Home",
                  params: {
                    screen: "Tracker",
                    params: {
                      screen: "ChinaTrackerReport",
                      params: {
                        screen: "ChinaTrackerScreen",
                      },
                    },
                  },
                });
              }
            }}
          >
            <AntDesign name="arrowleft" size={24} color={themeColors.text} />
          </TouchableOpacity>

          <View
            style={{
              flex: 1,
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              paddingRight: 15,
            }}
          >
            <Text
              selectable={true}
              style={{ fontWeight: "700", fontSize: 14.5 }}
            >
              {`${translateFields(
                "chinaordernumber",
                t,
                chinaTrackerTranslationMap,
              )}: ${selectedOrder?.OrderNumber1 || "AB130442"}`}
            </Text>
            <View
              style={{
                flex: 1,
                flexDirection: "row",
                justifyContent: "flex-end",
                gap: 2,
              }}
            >
              <TouchableOpacity
                onPress={() => {
                  setTrackerAction("flag");
                  setModalOpen(true);
                }}
              >
                <MaterialCommunityIcons
                  name="flag-outline"
                  size={20}
                  color={themeColors.text}
                  style={[styles.actionsIcons]}
                />
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => {
                  setTrackerAction("add");
                  setModalOpen(true);
                }}
              >
                <MaterialCommunityIcons
                  name="comment-outline"
                  size={20}
                  color={themeColors.text}
                  style={[styles.actionsIcons]}
                />
              </TouchableOpacity>

              <TouchableOpacity
                onPress={() => {
                  setTrackerAction("history");
                  setModalOpen(true);
                }}
              >
                <MaterialCommunityIcons
                  name="clock-outline"
                  size={20}
                  color={themeColors.text}
                  style={[styles.actionsIcons]}
                />
              </TouchableOpacity>

              <TouchableOpacity
                onPress={() => {
                  setTrackerAction("shipments");
                  setModalOpen(true);
                }}
              >
                <MaterialCommunityIcons
                  name="truck-outline"
                  size={20}
                  color={themeColors.text}
                  style={[styles.actionsIcons]}
                />
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    );
  };

  // Determine if we're on a small screen (mobile)
  const isMobileView = windowWidth < 768;

  return (
    <>
      {loading ? (
        <View style={styles.spinnerContainer}>
          <Spinner size="small" color="#74BBFF" />
        </View>
      ) : (
        <ScrollView contentContainerStyle={styles.container}>
          <DrawerHeader />

          {modalOpen && trackerAction === "add" && (
            <TrackerEnquiryModal
              onClose={onClose}
              visible={modalOpen}
              order={selectedOrder}
            />
          )}
          {modalOpen && trackerAction === "history" && (
            <TrackerHistoryModal
              onClose={onClose}
              visible={modalOpen}
              order={selectedOrder}
            />
          )}

          {modalOpen && trackerAction === "flag" && (
            <ChinaTrackerFlagModal
              onClose={onClose}
              visible={modalOpen}
              order={selectedOrder}
            />
          )}

          {modalOpen && trackerAction === "shipments" && (
            <ChinaShipmentsListModal
              onClose={onClose}
              visible={modalOpen}
              order={selectedOrder}
            />
          )}

          <View
            style={[
              styles.contentContainer,
              isMobileView && styles.contentContainerMobile,
            ]}
          >
            {/* Order Details */}
            <View
              style={[
                styles.orderDetails,
                isMobileView ? styles.orderDetailsMobile : null,
              ]}
            >
              {/* Third Box */}
              <View
                style={[
                  styles.detailBox,
                  isMobileView ? styles.detailBoxMobile : null,
                ]}
              >
                <View style={styles.detailRow}>
                  <Text selectable={true} style={styles.labelText}>
                    {translateFields(
                      "OrderStatus",
                      t,
                      chinaTrackerTranslationMap,
                    )}
                  </Text>
                  <View
                    style={[
                      styles.statusContainer,
                      { backgroundColor: themeColors.surfaceSubtle },
                    ]}
                  >
                    {selectedOrder?.OrderStatus === "On Time" && (
                      <AntDesign name="check" size={16} color="green" />
                    )}
                    <Text
                      selectable={true}
                      style={{
                        color: getStatusColor(selectedOrder?.OrderStatus, themeColors.text),
                        marginLeft: 5,
                        fontWeight: "600",
                      }}
                    >
                      {selectedOrder?.OrderStatus === "At Risk"
                        ? t("atRisk")
                        : selectedOrder?.OrderStatus === "On Time"
                          ? t("onTime")
                          : selectedOrder?.OrderStatus === "Delayed"
                            ? t("delayed")
                            : selectedOrder?.OrderStatus === "Planned"
                              ? t("planned")
                              : selectedOrder?.OrderStatus === "Interrupted"
                                ? t("interrupted")
                                : selectedOrder?.OrderStatus === "No Data"
                                  ? t("noData")
                                  : selectedOrder?.OrderStatus === "Cancelled"
                                    ? t("cancelled")
                                    : selectedOrder?.OrderStatus === "Completed"
                                      ? t("completed")
                                      : t("closed")}
                    </Text>
                  </View>
                </View>

                <View style={styles.detailRow}>
                  <Text selectable={true} style={styles.labelText}>
                    {translateFields(
                      "PriorityOrder",
                      t,
                      chinaTrackerTranslationMap,
                    )}
                  </Text>
                  <View
                    style={[
                      styles.statusContainer,
                      { backgroundColor: themeColors.surfaceSubtle },
                    ]}
                  >
                    <Text
                      selectable={true}
                      style={{
                        color: getStatusColor(selectedOrder?.PriorityOrder, themeColors.text),
                        marginLeft: 5,
                        fontWeight: "600",
                      }}
                    >
                      {selectedOrder?.PriorityOrder === "High"
                        ? t("high")
                        : selectedOrder?.PriorityOrder === "Medium"
                          ? t("medium")
                          : selectedOrder?.PriorityOrder === "Low"
                            ? t("low")
                            : t("normal")}
                    </Text>
                  </View>
                </View>
              </View>

              {/* First Box */}
              <View
                style={[
                  styles.detailBox,
                  isMobileView ? styles.detailBoxMobile : null,
                ]}
              >
                <View style={styles.detailRow}>
                  <Text selectable={true} style={styles.labelText}>
                    {translateFields(
                      "OrderNumber1",
                      t,
                      chinaTrackerTranslationMap,
                    )}
                  </Text>
                  <Text selectable={true} style={styles.valueText}>
                    {selectedOrder?.OrderNumber1}
                  </Text>
                </View>
                <View style={styles.detailRow}>
                  <Text selectable={true} style={styles.labelText}>
                    {translateFields("MSRef", t, chinaTrackerTranslationMap)}
                  </Text>
                  <Text selectable={true} style={styles.valueText}>
                    {selectedOrder?.MSRef}
                  </Text>
                </View>
                <View style={styles.detailRow}>
                  <Text selectable={true} style={styles.labelText}>
                    {translateFields(
                      "allocateProductionLine",
                      t,
                      chinaTrackerTranslationMap,
                    )}
                  </Text>
                  <Text selectable={true} style={styles.valueText}>
                    {selectedOrder?.allocateProductionLine}
                  </Text>
                </View>
                <View style={styles.detailRow}>
                  <Text selectable={true} style={styles.labelText}>
                    {translateFields(
                      "StateDelivery",
                      t,
                      chinaTrackerTranslationMap,
                    )}
                  </Text>
                  <Text selectable={true} style={styles.valueText}>
                    {selectedOrder?.StateDelivery}
                  </Text>
                </View>
                <View style={styles.detailRow}>
                  <Text selectable={true} style={styles.labelText}>
                    {translateFields(
                      "OrderDate",
                      t,
                      chinaTrackerTranslationMap,
                    )}
                  </Text>
                  <Text selectable={true} style={styles.valueText}>
                    {selectedOrder?.OrderDate}
                  </Text>
                </View>
              </View>

              {/* Third Box */}
              <View
                style={[
                  styles.detailBox,
                  isMobileView ? styles.detailBoxMobile : null,
                ]}
              >
                <View style={styles.detailRow}>
                  <Text selectable={true} style={[styles.labelText]}>
                    {translateFields("ItemCode", t, chinaTrackerTranslationMap)}
                  </Text>
                  <Text selectable={true} style={styles.valueText}>
                    {selectedOrder?.ItemCode || ""}
                  </Text>
                </View>
                <View style={styles.detailRow}>
                  <Text selectable={true} style={styles.labelText}>
                    {translateFields("ItemName", t, chinaTrackerTranslationMap)}
                  </Text>
                  <Text selectable={true} style={styles.valueText}>
                    {selectedOrder?.ItemName}
                  </Text>
                </View>
              </View>

              {/* Second Box */}
              <View
                style={[
                  styles.detailBox,
                  isMobileView ? styles.detailBoxMobile : null,
                ]}
              >
                <View style={styles.detailRow}>
                  <Text selectable={true} style={[styles.labelText]}>
                    {translateFields(
                      "ArtworkNo",
                      t,
                      chinaTrackerTranslationMap,
                    )}
                  </Text>
                  <Text selectable={true} style={styles.valueText}>
                    {selectedOrder?.ArtworkNo || ""}
                  </Text>
                </View>
                <View style={styles.detailRow}>
                  <Text selectable={true} style={styles.labelText}>
                    {translateFields(
                      "RequestDate",
                      t,
                      chinaTrackerTranslationMap,
                    )}
                  </Text>
                  <Text selectable={true} style={styles.valueText}>
                    {selectedOrder?.RequestDate}
                  </Text>
                </View>
                <View style={styles.detailRow}>
                  <Text selectable={true} style={styles.labelText}>
                    {translateFields(
                      "TotalOrdered",
                      t,
                      chinaTrackerTranslationMap,
                    )}
                  </Text>
                  <Text selectable={true} style={styles.valueText}>
                    {selectedOrder?.TotalOrdered?.toLocaleString()} Pieces
                  </Text>
                </View>
                <View style={styles.detailRow}>
                  <Text selectable={true} style={styles.labelText}>
                    {translateFields(
                      "QtyToShip",
                      t,
                      chinaTrackerTranslationMap,
                    )}
                  </Text>
                  <Text selectable={true} style={styles.valueText}>
                    {selectedOrder?.QtyToShip.toLocaleString()} Pieces
                  </Text>
                </View>
                <View style={styles.detailRow}>
                  <Text selectable={true} style={styles.labelText}>
                    {translateFields(
                      "FulfillmentPercent",
                      t,
                      chinaTrackerTranslationMap,
                    )}
                  </Text>
                  <View style={styles.progressBar}>
                    <View
                      style={[styles.progressFilled, { flex: progressBar }]}
                    />
                    <View
                      style={[
                        styles.progressEmpty,
                        { flex: 100 - selectedOrder?.FulfillmentPercent },
                      ]}
                    />
                  </View>
                </View>
              </View>
            </View>

            {/* Vertical Line - Only show on desktop */}
            {!isMobileView && <View style={styles.verticalLine}></View>}

            {/* Tracking History */}
            <View
              style={[
                styles.trackingContainer,
                isMobileView ? styles.trackingContainerMobile : null,
              ]}
            >
              {selectedOrder?.details?.map((detail, index) => {
                return (
                  <View style={styles.modalContainer}>
                    <View key={index} style={{ backgroundColor: "transparent" }}>
                      <View style={styles.detailRow2}>
                        <Text selectable={true} style={[styles.labelText]}>
                          {translateFields(
                            "runOrder",
                            t,
                            chinaTrackerTranslationMap,
                          )}
                        </Text>
                        <Text selectable={true} style={styles.valueText}>
                          {detail?.runOrder || ""}
                        </Text>
                      </View>

                      <View style={styles.detailRow2}>
                        <Text selectable={true} style={[styles.labelText]}>
                          {translateFields(
                            "labelOrder",
                            t,
                            chinaTrackerTranslationMap,
                          )}
                        </Text>

                        <Text selectable={true} style={styles.valueText}>
                          {detail.labelOrder === true ? (
                            <MaterialCommunityIcons
                              name="check"
                              size={20}
                              color="green"
                              style={[styles.actionsIcons]}
                            />
                          ) : (
                            <MaterialCommunityIcons
                              name="close"
                              size={20}
                              color="red"
                              style={[styles.actionsIcons]}
                            />
                          )}
                        </Text>
                      </View>

                      <View style={styles.detailRow2}>
                        <Text selectable={true} style={[styles.labelText]}>
                          {translateFields(
                            "directShipment",
                            t,
                            chinaTrackerTranslationMap,
                          )}
                        </Text>

                        <Text selectable={true} style={styles.valueText}>
                          {detail.directShipment === true ? (
                            <MaterialCommunityIcons
                              name="check"
                              size={20}
                              color="green"
                              style={[styles.actionsIcons]}
                            />
                          ) : (
                            <MaterialCommunityIcons
                              name="close"
                              size={20}
                              color="red"
                              style={[styles.actionsIcons]}
                            />
                          )}
                        </Text>
                      </View>

                      <View style={styles.detailRow2}>
                        <Text selectable={true} style={[styles.labelText]}>
                          {translateFields(
                            "soakarPad",
                            t,
                            chinaTrackerTranslationMap,
                          )}
                        </Text>

                        <Text selectable={true} style={styles.valueText}>
                          {detail.soakarPad === true ? (
                            <MaterialCommunityIcons
                              name="check"
                              size={20}
                              color="green"
                              style={[styles.actionsIcons]}
                            />
                          ) : (
                            <MaterialCommunityIcons
                              name="close"
                              size={20}
                              color="red"
                              style={[styles.actionsIcons]}
                            />
                          )}
                        </Text>
                      </View>

                      <View style={styles.detailRow2}>
                        <Text selectable={true} style={[styles.labelText]}>
                          {translateFields(
                            "PlannedQty",
                            t,
                            chinaTrackerTranslationMap,
                          )}
                        </Text>
                        <Text selectable={true} style={styles.valueText}>
                          {detail?.plannedQty?.toLocaleString() || ""}
                        </Text>
                      </View>

                      <View style={styles.detailRow2}>
                        <Text selectable={true} style={[styles.labelText]}>
                          {translateFields(
                            "Duration",
                            t,
                            chinaTrackerTranslationMap,
                          )}
                        </Text>
                        <Text selectable={true} style={styles.valueText}>
                          {detail?.duration || ""}
                        </Text>
                      </View>

                      <View style={styles.detailRow2}>
                        <Text selectable={true} style={[styles.labelText]}>
                          {translateFields(
                            "planStartDay",
                            t,
                            chinaTrackerTranslationMap,
                          )}
                        </Text>
                        <Text selectable={true} style={styles.valueText}>
                          {detail?.planStartDay &&
                            formatLocalDate(detail?.planStartDay)}
                        </Text>
                      </View>

                      <View style={styles.detailRow2}>
                        <Text selectable={true} style={[styles.labelText]}>
                          {translateFields(
                            "actualStartDay",
                            t,
                            chinaTrackerTranslationMap,
                          )}
                        </Text>
                        <Text selectable={true} style={styles.valueText}>
                          {detail?.actualStartDay &&
                            formatLocalDate(detail?.actualStartDay)}
                        </Text>
                      </View>

                      <View style={styles.detailRow2}>
                        <Text selectable={true} style={[styles.labelText]}>
                          {translateFields(
                            "ESTCompletionDate",
                            t,
                            chinaTrackerTranslationMap,
                          )}
                        </Text>
                        <Text selectable={true} style={styles.valueText}>
                          {detail?.ESTCompletionDate &&
                            formatLocalDate(detail?.ESTCompletionDate)}
                        </Text>
                      </View>

                      <View style={styles.detailRow2}>
                        <Text selectable={true} style={[styles.labelText]}>
                          {translateFields(
                            "actualCompletionDate",
                            t,
                            chinaTrackerTranslationMap,
                          )}
                        </Text>
                        <Text selectable={true} style={styles.valueText}>
                          {detail?.actualCompletionDate &&
                            formatLocalDate(detail?.actualCompletionDate)}
                        </Text>
                      </View>

                      <View style={styles.detailRow2}>
                        <Text selectable={true} style={[styles.labelText]}>
                          {translateFields(
                            "shippingDeadline",
                            t,
                            chinaTrackerTranslationMap,
                          )}
                        </Text>
                        <Text selectable={true} style={styles.valueText}>
                          {detail?.shippingDeadline &&
                            formatLocalDate(detail?.shippingDeadline)}
                        </Text>
                      </View>

                      <View style={styles.detailRow2}>
                        <Text selectable={true} style={[styles.labelText]}>
                          {translateFields(
                            "ETD",
                            t,
                            chinaTrackerTranslationMap,
                          )}
                        </Text>
                        <Text selectable={true} style={styles.valueText}>
                          {detail?.ETD && formatLocalDate(detail?.ETD)}
                        </Text>
                      </View>

                      <View style={styles.detailRow2}>
                        <Text selectable={true} style={[styles.labelText]}>
                          {translateFields(
                            "ETA",
                            t,
                            chinaTrackerTranslationMap,
                          )}
                        </Text>
                        <Text selectable={true} style={styles.valueText}>
                          {detail?.ETA && formatLocalDate(detail?.ETA)}
                        </Text>
                      </View>

                      <View style={styles.detailRow2}>
                        <Text selectable={true} style={[styles.labelText]}>
                          {translateFields(
                            "ExpectedArrival",
                            t,
                            chinaTrackerTranslationMap,
                          )}
                        </Text>
                        <Text selectable={true} style={styles.valueText}>
                          {detail?.ExpectedArrival &&
                            formatLocalDate(detail?.ExpectedArrival)}
                        </Text>
                      </View>
                    </View>
                  </View>
                );
              })}
            </View>
          </View>
        </ScrollView>
      )}
    </>
  );
};

// Function to get status color
const getStatusColor = (status: string, defaultColor: string) => {
  switch (status) {
    case "On Time":
      return "green";
    case "At Risk":
      return "orange";
    case "Delayed":
      return "red";
    default:
      return defaultColor; // Default Black
  }
};

function createStyles(colors: any) {
  return StyleSheet.create({
  container: {
    flexGrow: 1,
    backgroundColor: colors.surface,
  },
  spinnerContainer: {
    position: "absolute",
    top: 100,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: colors.surface,
  },
  contentContainer: {
    padding: 10,
    flexDirection: "row",
    flexWrap: "wrap",
  },
  contentContainerMobile: {
    flexDirection: "column",
  },
  orderDetails: {
    width: "40%",
    paddingRight: 10,
  },
  orderDetailsMobile: {
    width: "100%",
    paddingRight: 0,
  },
  detailBox: {
    backgroundColor: colors.surfaceSubtle,
    borderRadius: 14,
    padding: 15,
    marginBottom: 10,
    // width: 418,
    minWidth: 200,
    marginLeft: "25%",
  },
  detailBoxMobile: {
    width: "100%",
    marginLeft: 0,
  },
  detailRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 8,
    backgroundColor: "transparent",
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  detailRow2: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 8,
    backgroundColor: "transparent",
    alignItems: "flex-start",
    flexWrap: "wrap",
    paddingLeft: 5,
    paddingRight: 5,
  },
  labelText: {
    color: colors.text,
    fontWeight: "700",
    fontSize: 15,
    width: "30%",
  },
  valueText: {
    color: colors.text,
    fontWeight: "400",
    fontSize: 13,
    textAlign: "right",
    flexWrap: "wrap",
    width: "65%",
  },
  progressBar: {
    width: 100,
    height: 10,
    flexDirection: "row",
    borderRadius: 5,
    overflow: "hidden",
  },
  progressFilled: {
    flex: 1,
    backgroundColor: "#56B7FD",
  },
  progressEmpty: {
    flex: 2,
    backgroundColor: colors.surface,
  },
  statusContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  // statusText: {
  //   color: "green",
  //   marginLeft: 5,
  //   fontWeight: "600",
  // },
  deliveryProgressContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 4,
    borderColor: "#56B7FD",
    justifyContent: "center",
    alignItems: "center",
    transform: [{ rotate: "-90deg" }],
  },
  deliveryProgress: {
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 4,
    borderColor: "#2196F3",
    borderLeftColor: "transparent",
    borderBottomColor: "transparent",
  },
  trackingContainer: {
    width: "55%",
    paddingLeft: 10,
  },
  trackingContainerMobile: {
    width: "100%",
    paddingLeft: 0,
    marginTop: 10,
  },
  verticalLine: {
    width: 1,
    backgroundColor: colors.border,
    marginHorizontal: 10,
  },
  modalContainer: {
    marginTop: 10,
    backgroundColor: colors.surfaceSubtle,
    padding: 20,
    width: "100%",
    elevation: 5,
    borderRadius: 10,
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
    textAlign: "left",
    marginBottom: 15,
  },
  scrollContent: {
    paddingBottom: 20,
  },
  entry: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 25,
  },
  iconContainer: {
    alignItems: "center",
    width: 100,
  },
  date: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#444",
    marginBottom: 4,
  },
  line: {
    width: 2,
    height: 30,
    backgroundColor: colors.border,
    marginTop: 4,
  },
  detailsContainer: {
    flex: 1,
    paddingLeft: 15,
    paddingVertical: 5,
    backgroundColor: "transparent",
    borderRadius: 8,
    padding: 10,
  },
  status: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#222",
    marginBottom: 4,
  },
  errorStatus: {
    color: "red",
  },
  detailsText: {
    fontSize: 12,
    color: "#555",
    lineHeight: 18,
  },
  drawerHeaderStyle: {
    top: 0,
    width: "100%",
    zIndex: 999,
    backgroundColor: colors.surface,
    height: 40,
    borderBottomWidth: 2,
    borderColor: "grey",
    justifyContent: "center",
  },
  textCenter: {
    textAlign: "center",
    fontWeight: "bold",
  },
  actionsIcons: {
    paddingHorizontal: 4,
    cursor: "pointer",
  },
});
}

export default ChinaTrackerDetailsReport;
